import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  Pagination,
  IconButton,
  Typography,
  Avatar,
  useMediaQuery,
  useTheme,
  Drawer,
  CircularProgress,
  Alert,
  Snackbar,
  Button,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Chip,
  Grid,
  Stack,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
  Badge,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import {
  Delete,
  Add,
  Search,
  FilterList,
  ArrowBack,
  Close,
  LocalOffer,
  CalendarToday,
  AttachMoney,
  Refresh,
  Visibility,
  Whatshot,
  Edit,
  Percent,
} from '@mui/icons-material';

import Slidemenu from '../../components/admin/adminSlidemenu.tsx';
import AdminHeader from '../../components/admin/adminHeader.tsx';
import {
  useListCouponsQuery,
  useCreateCouponMutation,
  useUpdateCouponMutation,
  useDeleteCouponMutation,
  Coupon,
  CouponFormData,
} from '../../features/admin/couponApi.ts';

interface EnrichedCoupon extends Coupon {
  statusDisplay?: string;
  discountDisplay?: string;
}

const CouponManagement = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [mobileOpen, setMobileOpen] = useState(false);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [selectedCoupon, setSelectedCoupon] = useState<Coupon | null>(null);
  const [enrichedCoupons, setEnrichedCoupons] = useState<EnrichedCoupon[]>([]);
  const [formData, setFormData] = useState<CouponFormData>({
    code: '',
    discount_type: 'percentage',
    discount_value: '',
    min_amount: '',
    max_discount: '',
    start_date: '',
    end_date: '',
    usage_limit: 0,
  });
  const [status, setStatus] = useState<number>(0); // 0-Active, 1-Inactive
  const [formErrors, setFormErrors] = useState<Partial<CouponFormData>>({});
  const [snackbar, setSnackbar] = useState({ 
    open: false, 
    message: '', 
    severity: 'success' as 'success' | 'error' 
  });
  const [tabValue, setTabValue] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');

  // API hooks
  const { 
    data, 
    isLoading, 
    error, 
    isError, 
    refetch 
  } = useListCouponsQuery();

  const [createCoupon, { isLoading: isCreating }] = useCreateCouponMutation();
  const [updateCoupon, { isLoading: isUpdating }] = useUpdateCouponMutation();
  const [deleteCoupon, { isLoading: isDeleting }] = useDeleteCouponMutation();

  // Handle API response structure
  const coupons = data?.data || [];

  // Enrich coupons with display properties
  useEffect(() => {
    if (coupons.length > 0) {
      const enriched = coupons.map((coupon: Coupon) => {
        const now = new Date();
        const startDate = new Date(coupon.start_date);
        const endDate = new Date(coupon.end_date);
        
        let statusDisplay = 'Active';
        if (now < startDate) {
          statusDisplay = 'Upcoming';
        } else if (now > endDate) {
          statusDisplay = 'Expired';
        } else if (coupon.status === '1') {
          statusDisplay = 'Inactive';
        }

        const discountDisplay = coupon.discount_type === 'flat' 
          ? `AED ${coupon.discount_value}`
          : `${coupon.discount_value}%`;

        return {
          ...coupon,
          statusDisplay,
          discountDisplay,
        };
      });
      setEnrichedCoupons(enriched);
    } else {
      setEnrichedCoupons([]);
    }
  }, [coupons]);

  // Filter coupons based on search and tab
  const filteredCoupons = enrichedCoupons.filter(coupon => {
    const matchesSearch = coupon.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         coupon.discountDisplay?.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (tabValue === 0) return matchesSearch; // All
    if (tabValue === 1) return matchesSearch && coupon.statusDisplay === 'Active';
    if (tabValue === 2) return matchesSearch && coupon.statusDisplay === 'Upcoming';
    if (tabValue === 3) return matchesSearch && coupon.statusDisplay === 'Expired';
    if (tabValue === 4) return matchesSearch && coupon.statusDisplay === 'Inactive';
    
    return matchesSearch;
  });

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleRefresh = () => {
    refetch();
    setSnackbar({
      open: true,
      message: 'Coupons refreshed successfully!',
      severity: 'success'
    });
  };

  const handleOpenAddDialog = () => {
    setOpenAddDialog(true);
    setFormData({
      code: '',
      discount_type: 'percentage',
      discount_value: '',
      min_amount: '',
      max_discount: '',
      start_date: '',
      end_date: '',
      usage_limit: 0,
    });
    setStatus(0); // Default to Active
    setFormErrors({});
  };

  const handleCloseAddDialog = () => {
    setOpenAddDialog(false);
    setFormData({
      code: '',
      discount_type: 'percentage',
      discount_value: '',
      min_amount: '',
      max_discount: '',
      start_date: '',
      end_date: '',
      usage_limit: 0,
    });
    setStatus(0);
    setFormErrors({});
  };

  const handleOpenEditDialog = (coupon: Coupon) => {
    setSelectedCoupon(coupon);
    setFormData({
      code: coupon.code,
      discount_type: coupon.discount_type as 'flat' | 'percentage',
      discount_value: coupon.discount_value,
      min_amount: coupon.min_amount,
      max_discount: coupon.max_discount,
      start_date: coupon.start_date,
      end_date: coupon.end_date,
      usage_limit: coupon.usage_limit || 0,
    });
    // Set status based on coupon status (convert string to number if needed)
    setStatus(coupon.status ? parseInt(coupon.status) : 0);
    setOpenEditDialog(true);
    setFormErrors({});
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setSelectedCoupon(null);
    setFormData({
      code: '',
      discount_type: 'percentage',
      discount_value: '',
      min_amount: '',
      max_discount: '',
      start_date: '',
      end_date: '',
      usage_limit: 0,
    });
    setStatus(0);
    setFormErrors({});
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (formErrors[name as keyof CouponFormData]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const handleSelectChange = (e: SelectChangeEvent) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleStatusChange = (e: SelectChangeEvent<number>) => {
    setStatus(Number(e.target.value));
  };

  const validateForm = (): boolean => {
    const errors: Partial<CouponFormData> = {};

    if (!formData.code.trim()) {
      errors.code = 'Coupon code is required';
    }
    if (!formData.discount_value) {
      errors.discount_value = 'Discount value is required';
    }
    if (!formData.start_date) {
      errors.start_date = 'Start date is required';
    }
    if (!formData.end_date) {
      errors.end_date = 'End date is required';
    }
    if (formData.start_date && formData.end_date && new Date(formData.end_date) <= new Date(formData.start_date)) {
      errors.end_date = 'End date must be after start date';
    }
    if (formData.discount_type === 'percentage' && (!formData.min_amount || !formData.max_discount)) {
      if (!formData.min_amount) errors.min_amount = 'Minimum amount is required for percentage discounts';
      if (!formData.max_discount) errors.max_discount = 'Maximum discount is required for percentage discounts';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreateCoupon = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      await createCoupon(formData).unwrap();
      setSnackbar({
        open: true,
        message: 'Coupon created successfully!',
        severity: 'success'
      });
      handleCloseAddDialog();
      refetch();
    } catch (err: any) {
      console.error('Failed to create coupon:', err);
      setSnackbar({
        open: true,
        message: err?.data?.message || 'Error creating coupon. Please try again.',
        severity: 'error'
      });
    }
  };

  const handleUpdateCoupon = async () => {
    if (!validateForm() || !selectedCoupon) {
      return;
    }

    try {
      // Prepare the update data with status field
      const updateData = {
        id: selectedCoupon.id,
        ...formData,
        status: status // Include the status field (0 or 1)
      };

      console.log('Updating coupon with data:', updateData); // For debugging

      await updateCoupon(updateData).unwrap();
      setSnackbar({
        open: true,
        message: 'Coupon updated successfully!',
        severity: 'success'
      });
      handleCloseEditDialog();
      refetch();
    } catch (err: any) {
      console.error('Failed to update coupon:', err);
      console.error('Error details:', err?.data);
      setSnackbar({
        open: true,
        message: err?.data?.message || 'Error updating coupon. Please try again.',
        severity: 'error'
      });
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this coupon?')) {
      try {
        await deleteCoupon(id).unwrap();
        setSnackbar({
          open: true,
          message: 'Coupon deleted successfully!',
          severity: 'success'
        });
        refetch();
      } catch (err: any) {
        console.error('Failed to delete coupon:', err);
        setSnackbar({
          open: true,
          message: err?.data?.message || 'Error deleting coupon. Please try again.',
          severity: 'error'
        });
      }
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const formatDateTime = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDateForInput = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toISOString().slice(0, 16);
  };

  const getStatusColor = (status: string) => {
    const statusLower = status?.toLowerCase();
    if (statusLower === 'active') {
      return { bgcolor: '#d1f4e0', color: '#00783e', fontWeight: 600 };
    } else if (statusLower === 'upcoming') {
      return { bgcolor: '#e3f2fd', color: '#1565c0', fontWeight: 600 };
    } else if (statusLower === 'expired') {
      return { bgcolor: '#ffcdd2', color: '#c62828', fontWeight: 600 };
    } else if (statusLower === 'inactive') {
      return { bgcolor: '#e4e5e7', color: '#616161', fontWeight: 600 };
    }
    return { bgcolor: '#e4e5e7', color: '#616161', fontWeight: 600 };
  };

  const formatCurrency = (amount: number | string) => {
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'AED',
    }).format(numAmount);
  };

  const isLoadingData = isLoading;

  // Count coupons by status for tabs
  const statusCounts = {
    all: enrichedCoupons.length,
    active: enrichedCoupons.filter(c => c.statusDisplay === 'Active').length,
    upcoming: enrichedCoupons.filter(c => c.statusDisplay === 'Upcoming').length,
    expired: enrichedCoupons.filter(c => c.statusDisplay === 'Expired').length,
    inactive: enrichedCoupons.filter(c => c.statusDisplay === 'Inactive').length,
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: '#f1f2f4' }}>
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        <Box sx={{ width: '250px', flexShrink: 0, display: { xs: 'none', md: 'block' } }}>
          <Slidemenu mobileOpen={false} onToggle={handleDrawerToggle} />
        </Box>
        
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { width: 250 },
          }}
        >
          <Slidemenu onToggle={handleDrawerToggle} mobileOpen={false} />
        </Drawer>
        
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <AdminHeader onToggleMenu={handleDrawerToggle} />
          <Box
            sx={{
              flexGrow: 1,
              overflow: 'auto',
              backgroundColor: '#f1f2f4',
            }}
          >
            <Box sx={{ maxWidth: 1600, mx: 'auto', p: 3 }}>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                mb: 2
              }}>
                <Typography variant="h5" sx={{ fontWeight: 600, color: '#202223' }}>
                  Coupon Management
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined"
                    startIcon={<Refresh />}
                    onClick={handleRefresh}
                    disabled={isLoadingData}
                    sx={{
                      textTransform: 'none',
                      borderColor: '#c9cccf',
                      color: '#202223',
                      borderRadius: '8px',
                      fontWeight: 500,
                      '&:hover': {
                        borderColor: '#202223',
                        bgcolor: 'transparent',
                      }
                    }}
                  >
                    Refresh
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={handleOpenAddDialog}
                    sx={{
                      bgcolor: '#008060',
                      color: 'white',
                      textTransform: 'none',
                      fontWeight: 600,
                      borderRadius: '8px',
                      px: 2.5,
                      '&:hover': { bgcolor: '#006e52' }
                    }}
                  >
                    Create Coupon
                  </Button>
                </Box>
              </Box>

              <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5', overflow: 'hidden' }}>
                <Box sx={{ borderBottom: '1px solid #e1e3e5' }}>
                  <Tabs 
                    value={tabValue} 
                    onChange={(e, v) => setTabValue(v)}
                    sx={{
                      px: 2,
                      '& .MuiTab-root': {
                        textTransform: 'none',
                        fontWeight: 500,
                        fontSize: '14px',
                        minHeight: 48,
                        color: '#6d7175',
                      },
                      '& .Mui-selected': {
                        color: '#202223',
                        fontWeight: 600,
                      },
                      '& .MuiTabs-indicator': {
                        backgroundColor: '#008060',
                        height: 3,
                      }
                    }}
                  >
                    <Tab label={`All coupons (${statusCounts.all})`} />
                    <Tab label={`Active (${statusCounts.active})`} />
                    <Tab label={`Upcoming (${statusCounts.upcoming})`} />
                    <Tab label={`Expired (${statusCounts.expired})`} />
                    <Tab label={`Inactive (${statusCounts.inactive})`} />
                  </Tabs>
                </Box>

                <Box sx={{ p: 2, borderBottom: '1px solid #e1e3e5', display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <TextField
                    size="small"
                    placeholder="Search coupons..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    sx={{ 
                      minWidth: 300,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        bgcolor: 'white',
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search fontSize="small" sx={{ color: '#6d7175' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Button
                    variant="outlined"
                    startIcon={<FilterList />}
                    sx={{
                      textTransform: 'none',
                      borderColor: '#c9cccf',
                      color: '#202223',
                      borderRadius: '8px',
                      fontWeight: 500,
                      '&:hover': {
                        borderColor: '#202223',
                        bgcolor: 'transparent',
                      }
                    }}
                  >
                    More filters
                  </Button>
                </Box>

                {isLoadingData && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300, bgcolor: 'white' }}>
                    <CircularProgress size={32} sx={{ color: '#008060' }} />
                  </Box>
                )}

                {isError && !isLoadingData && (
                  <Box sx={{ p: 3, bgcolor: 'white' }}>
                    <Alert severity="error">
                      Failed to load coupons. Please try again.
                      <Button 
                        color="inherit" 
                        size="small" 
                        onClick={() => refetch()}
                        sx={{ ml: 1 }}
                      >
                        Retry
                      </Button>
                    </Alert>
                  </Box>
                )}

                {!isLoadingData && !isError && filteredCoupons.length === 0 && (
                  <Box sx={{ textAlign: 'center', py: 8, bgcolor: 'white' }}>
                    <LocalOffer sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontWeight: 500 }}>
                      No coupons found
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      Get started by creating your first coupon
                    </Typography>
                    <Button
                      variant="contained"
                      onClick={handleOpenAddDialog}
                      sx={{
                        bgcolor: '#008060',
                        textTransform: 'none',
                        fontWeight: 600,
                        '&:hover': { bgcolor: '#006e52' }
                      }}
                    >
                      Create Coupon
                    </Button>
                  </Box>
                )}

                {!isLoadingData && filteredCoupons.length > 0 && (
                  <Box sx={{ bgcolor: 'white' }}>
                    {/* Desktop Table View */}
                    {!isMobile && (
                      <TableContainer>
                        <Table>
                          <TableHead>
                            <TableRow sx={{ bgcolor: '#fafbfb' }}>
                              <TableCell sx={{ fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', color: '#6d7175' }}>
                                Code
                              </TableCell>
                              <TableCell sx={{ fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', color: '#6d7175' }}>
                                Discount
                              </TableCell>
                              <TableCell sx={{ fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', color: '#6d7175' }}>
                                Min Amount
                              </TableCell>
                              <TableCell sx={{ fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', color: '#6d7175' }}>
                                Max Discount
                              </TableCell>
                              <TableCell sx={{ fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', color: '#6d7175' }}>
                                Valid Period
                              </TableCell>
                              <TableCell sx={{ fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', color: '#6d7175' }}>
                                Usage
                              </TableCell>
                              <TableCell sx={{ fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', color: '#6d7175' }}>
                                Status
                              </TableCell>
                              <TableCell sx={{ fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', color: '#6d7175', textAlign: 'right' }}>
                                Actions
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {filteredCoupons.map((coupon) => (
                              <TableRow 
                                key={coupon.id}
                                sx={{ 
                                  '&:hover': { bgcolor: '#f9fafb' },
                                  transition: 'background-color 0.15s ease',
                                }}
                              >
                                <TableCell>
                                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#202223' }}>
                                    {coupon.code}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {coupon.discount_type === 'percentage' ? (
                                      <Percent sx={{ fontSize: 16, color: '#6d7175' }} />
                                    ) : (
                                      <AttachMoney sx={{ fontSize: 16, color: '#6d7175' }} />
                                    )}
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                      {coupon.discountDisplay}
                                    </Typography>
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2">
                                    {coupon.min_amount ? formatCurrency(coupon.min_amount) : 'No minimum'}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2">
                                    {coupon.max_discount ? formatCurrency(coupon.max_discount) : '-'}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '12px' }}>
                                    {formatDateTime(coupon.start_date)}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '12px' }}>
                                    to {formatDateTime(coupon.end_date)}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2">
                                    {coupon.used_count || 0} / {coupon.usage_limit || '∞'}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Chip 
                                    label={coupon.statusDisplay} 
                                    size="small"
                                    sx={{
                                      fontSize: '12px',
                                      ...getStatusColor(coupon.statusDisplay || '')
                                    }}
                                  />
                                </TableCell>
                                <TableCell align="right">
                                  <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleOpenEditDialog(coupon)}
                                      sx={{ 
                                        color: '#6d7175',
                                        '&:hover': { bgcolor: '#f1f2f4' }
                                      }}
                                    >
                                      <Edit fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleDelete(coupon.id)}
                                      disabled={isDeleting}
                                      sx={{ 
                                        color: '#6d7175',
                                        '&:hover': { bgcolor: '#f1f2f4' }
                                      }}
                                    >
                                      <Delete fontSize="small" />
                                    </IconButton>
                                  </Box>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}

                    {/* Mobile Card View */}
                    {isMobile && (
                      <Box sx={{ p: 2 }}>
                        {filteredCoupons.map((coupon) => (
                          <Card key={coupon.id} sx={{ mb: 2, p: 2, borderRadius: '12px', border: '1px solid #e1e3e5' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#202223' }}>
                                {coupon.code}
                              </Typography>
                              <Chip 
                                label={coupon.statusDisplay} 
                                size="small"
                                sx={{
                                  fontSize: '10px',
                                  ...getStatusColor(coupon.statusDisplay || '')
                                }}
                              />
                            </Box>
                            
                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 2 }}>
                              <Box>
                                <Typography variant="caption" color="text.secondary">
                                  Discount
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {coupon.discountDisplay}
                                </Typography>
                              </Box>
                              <Box>
                                <Typography variant="caption" color="text.secondary">
                                  Min Amount
                                </Typography>
                                <Typography variant="body2">
                                  {coupon.min_amount ? formatCurrency(coupon.min_amount) : 'No minimum'}
                                </Typography>
                              </Box>
                              <Box>
                                <Typography variant="caption" color="text.secondary">
                                  Max Discount
                                </Typography>
                                <Typography variant="body2">
                                  {coupon.max_discount ? formatCurrency(coupon.max_discount) : '-'}
                                </Typography>
                              </Box>
                              <Box>
                                <Typography variant="caption" color="text.secondary">
                                  Usage
                                </Typography>
                                <Typography variant="body2">
                                  {coupon.used_count || 0} / {coupon.usage_limit || '∞'}
                                </Typography>
                              </Box>
                            </Box>

                            <Box sx={{ mb: 2 }}>
                              <Typography variant="caption" color="text.secondary">
                                Valid Period
                              </Typography>
                              <Typography variant="body2" sx={{ fontSize: '12px' }}>
                                {formatDateTime(coupon.start_date)} to {formatDateTime(coupon.end_date)}
                              </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                              <IconButton
                                size="small"
                                onClick={() => handleOpenEditDialog(coupon)}
                                sx={{ 
                                  color: '#6d7175',
                                  '&:hover': { bgcolor: '#f1f2f4' }
                                }}
                              >
                                <Edit fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleDelete(coupon.id)}
                                disabled={isDeleting}
                                sx={{ 
                                  color: '#6d7175',
                                  '&:hover': { bgcolor: '#f1f2f4' }
                                }}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </Box>
                          </Card>
                        ))}
                      </Box>
                    )}
                  </Box>
                )}
              </Card>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Add Coupon Dialog */}
      <Dialog
        open={openAddDialog}
        onClose={handleCloseAddDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#202223' }}>
              Create New Coupon
            </Typography>
            <IconButton onClick={handleCloseAddDialog} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Coupon Code *"
              name="code"
              value={formData.code}
              onChange={handleInputChange}
              error={!!formErrors.code}
              helperText={formErrors.code}
              fullWidth
            />

            <FormControl fullWidth>
              <InputLabel>Discount Type *</InputLabel>
              <Select
                name="discount_type"
                value={formData.discount_type}
                label="Discount Type *"
                onChange={handleSelectChange}
              >
                <MenuItem value="percentage">Percentage</MenuItem>
                <MenuItem value="flat">Flat Amount</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Discount Value *"
              name="discount_value"
              type="number"
              value={formData.discount_value}
              onChange={handleInputChange}
              error={!!formErrors.discount_value}
              helperText={formErrors.discount_value}
              fullWidth
              InputProps={{
                endAdornment: formData.discount_type === 'percentage' ? (
                  <InputAdornment position="end">%</InputAdornment>
                ) : (
                  <InputAdornment position="end">AED</InputAdornment>
                ),
              }}
            />

            <TextField
              label="Minimum Amount"
              name="min_amount"
              type="number"
              value={formData.min_amount}
              onChange={handleInputChange}
              error={!!formErrors.min_amount}
              helperText={formErrors.min_amount || 'Minimum cart amount to apply coupon'}
              fullWidth
              InputProps={{
                startAdornment: <InputAdornment position="start">AED</InputAdornment>,
              }}
            />

            {formData.discount_type === 'percentage' && (
              <TextField
                label="Maximum Discount *"
                name="max_discount"
                type="number"
                value={formData.max_discount}
                onChange={handleInputChange}
                error={!!formErrors.max_discount}
                helperText={formErrors.max_discount || 'Maximum discount amount in AED'}
                fullWidth
                InputProps={{
                  startAdornment: <InputAdornment position="start">AED</InputAdornment>,
                }}
              />
            )}

            <TextField
              label="Usage Limit"
              name="usage_limit"
              type="number"
              value={formData.usage_limit}
              onChange={handleInputChange}
              helperText="Leave empty for unlimited usage"
              fullWidth
            />
            
            <TextField
              label="Start Date *"
              name="start_date"
              type="datetime-local"
              fullWidth
              value={formData.start_date}
              onChange={handleInputChange}
              error={!!formErrors.start_date}
              helperText={formErrors.start_date}
              InputLabelProps={{
                shrink: true,
              }}
            />
            
            <TextField
              label="End Date *"
              name="end_date"
              type="datetime-local"
              fullWidth
              value={formData.end_date}
              onChange={handleInputChange}
              error={!!formErrors.end_date}
              helperText={formErrors.end_date}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button
            onClick={handleCloseAddDialog}
            disabled={isCreating}
            sx={{ 
              textTransform: 'none',
              fontWeight: 600,
              color: '#202223',
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleCreateCoupon} 
            variant="contained"
            disabled={isCreating}
            sx={{
              bgcolor: '#008060',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': { bgcolor: '#006e52' }
            }}
          >
            {isCreating ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} color="inherit" />
                Creating...
              </Box>
            ) : (
              'Create Coupon'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Coupon Dialog */}
      <Dialog
        open={openEditDialog}
        onClose={handleCloseEditDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#202223' }}>
              Edit Coupon
            </Typography>
            <IconButton onClick={handleCloseEditDialog} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Coupon Code *"
              name="code"
              value={formData.code}
              onChange={handleInputChange}
              error={!!formErrors.code}
              helperText={formErrors.code}
              fullWidth
            />

            <FormControl fullWidth>
              <InputLabel>Discount Type *</InputLabel>
              <Select
                name="discount_type"
                value={formData.discount_type}
                label="Discount Type *"
                onChange={handleSelectChange}
              >
                <MenuItem value="percentage">Percentage</MenuItem>
                <MenuItem value="flat">Flat Amount</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Discount Value *"
              name="discount_value"
              type="number"
              value={formData.discount_value}
              onChange={handleInputChange}
              error={!!formErrors.discount_value}
              helperText={formErrors.discount_value}
              fullWidth
              InputProps={{
                endAdornment: formData.discount_type === 'percentage' ? (
                  <InputAdornment position="end">%</InputAdornment>
                ) : (
                  <InputAdornment position="end">AED</InputAdornment>
                ),
              }}
            />

            <TextField
              label="Minimum Amount"
              name="min_amount"
              type="number"
              value={formData.min_amount}
              onChange={handleInputChange}
              error={!!formErrors.min_amount}
              helperText={formErrors.min_amount || 'Minimum cart amount to apply coupon'}
              fullWidth
              InputProps={{
                startAdornment: <InputAdornment position="start">AED</InputAdornment>,
              }}
            />

            {formData.discount_type === 'percentage' && (
              <TextField
                label="Maximum Discount *"
                name="max_discount"
                type="number"
                value={formData.max_discount}
                onChange={handleInputChange}
                error={!!formErrors.max_discount}
                helperText={formErrors.max_discount || 'Maximum discount amount in AED'}
                fullWidth
                InputProps={{
                  startAdornment: <InputAdornment position="start">AED</InputAdornment>,
                }}
              />
            )}

            <TextField
              label="Usage Limit"
              name="usage_limit"
              type="number"
              value={formData.usage_limit}
              onChange={handleInputChange}
              helperText="Leave empty for unlimited usage"
              fullWidth
            />

            <FormControl fullWidth>
              <InputLabel>Status *</InputLabel>
              <Select
                value={status}
                label="Status *"
                onChange={handleStatusChange}
              >
                <MenuItem value={0}>Active</MenuItem>
                <MenuItem value={1}>Inactive</MenuItem>
              </Select>
            </FormControl>
            
            <TextField
              label="Start Date *"
              name="start_date"
              type="datetime-local"
              fullWidth
              value={formatDateForInput(formData.start_date)}
              onChange={handleInputChange}
              error={!!formErrors.start_date}
              helperText={formErrors.start_date}
              InputLabelProps={{
                shrink: true,
              }}
            />
            
            <TextField
              label="End Date *"
              name="end_date"
              type="datetime-local"
              fullWidth
              value={formatDateForInput(formData.end_date)}
              onChange={handleInputChange}
              error={!!formErrors.end_date}
              helperText={formErrors.end_date}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button
            onClick={handleCloseEditDialog}
            disabled={isUpdating}
            sx={{ 
              textTransform: 'none',
              fontWeight: 600,
              color: '#202223',
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleUpdateCoupon} 
            variant="contained"
            disabled={isUpdating}
            sx={{
              bgcolor: '#008060',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': { bgcolor: '#006e52' }
            }}
          >
            {isUpdating ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} color="inherit" />
                Updating...
              </Box>
            ) : (
              'Update Coupon'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CouponManagement;